import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChartLine, FaHistory, FaTrophy, FaArrowRight, FaGraduationCap } from 'react-icons/fa';
import XPDisplay from './XPDisplay';
import { getUserStats, getRecentQuizAttempts } from '../services/quizService';
import './Dashboard.css';

const Dashboard = ({ user }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);
    const [recentAttempts, setRecentAttempts] = useState([]);
    const [xpUpdateHandler, setXPUpdateHandler] = useState(null);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            setError(null);

            if (!user) {
                setError('Please sign in to view your dashboard.');
                setLoading(false);
                return;
            }

            const [userStats, attempts] = await Promise.all([
                getUserStats(user.uid),
                getRecentQuizAttempts(user.uid)
            ]);

            setStats(userStats);
            setRecentAttempts(attempts);
            setLoading(false);
        } catch (error) {
            console.error('Dashboard data fetch error:', error);
            setError(error.message || 'Failed to load dashboard data. Please try again later.');
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
    }, [user]);

    const handleXPUpdate = (handler) => {
        setXPUpdateHandler(() => handler);
    };

    const handleStartQuiz = () => {
        navigate('/quiz', { 
            state: { 
                fromDashboard: true,
                userId: user.uid 
            }
        });
    };

    const handleViewHistory = () => {
        navigate('/quiz/history', { 
            state: { 
                userId: user.uid 
            }
        });
    };

    const handleActivityClick = (attempt) => {
        navigate('/quiz', {
            state: {
                fromDashboard: true,
                userId: user.uid,
                subject: attempt.subject,
                grade: attempt.grade,
                topic: attempt.topic,
                subtopic: attempt.subtopic
            }
        });
    };

    if (loading) {
        return (
            <div className="dashboard">
                <div className="dashboard-header">
                    <div className="header-skeleton"></div>
                </div>
                <div className="stats-grid">
                    <div className="stat-card-skeleton"></div>
                    <div className="stat-card-skeleton"></div>
                    <div className="stat-card-skeleton"></div>
                </div>
                <div className="activity-section">
                    <div className="activity-skeleton"></div>
                    <div className="activity-skeleton"></div>
                    <div className="activity-skeleton"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard">
                <div className="dashboard-error">
                    <p>{error}</p>
                    {error !== 'Please sign in to view your dashboard.' && (
                        <button className="retry-button" onClick={fetchDashboardData}>
                            Retry
                        </button>
                    )}
                </div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="dashboard">
                <div className="dashboard-error">
                    <p>Please sign in to view your dashboard.</p>
                    <button onClick={() => navigate('/signin')} className="action-button primary">
                        Sign In
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard">
            <div className="dashboard-header">
                <div className="welcome-section">
                    <h1>Welcome back, {user?.displayName || 'Student'}!</h1>
                    <p className="subtitle">Track your progress and keep learning</p>
                </div>
                <XPDisplay user={user} onXPUpdate={handleXPUpdate} />
            </div>

            <div className="dashboard-actions">
                <button 
                    className="action-button primary"
                    onClick={handleStartQuiz}
                >
                    Start New Quiz
                    <FaArrowRight className="button-icon" />
                </button>
                <button 
                    className="action-button secondary"
                    onClick={handleViewHistory}
                >
                    View History
                    <FaHistory className="button-icon" />
                </button>
            </div>

            <div className="stats-section">
                <h2>Your Performance</h2>
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon">
                            <FaGraduationCap />
                        </div>
                        <div className="stat-content">
                            <h3>Quizzes Taken</h3>
                            <p className="stat-value">{stats?.totalQuizzes || 0}</p>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon">
                            <FaChartLine />
                        </div>
                        <div className="stat-content">
                            <h3>Average Score</h3>
                            <p className="stat-value">{stats?.averageScore || 0}%</p>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon">
                            <FaTrophy />
                        </div>
                        <div className="stat-content">
                            <h3>Best Score</h3>
                            <p className="stat-value">{stats?.bestScore || 0}%</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="recent-activity">
                <h2>Recent Activity</h2>
                {recentAttempts.length > 0 ? (
                    <div className="activity-list">
                        {recentAttempts.map((attempt, index) => (
                            <div 
                                key={index} 
                                className="activity-item"
                                onClick={() => handleActivityClick(attempt)}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="activity-icon">
                                    <FaHistory />
                                </div>
                                <div className="activity-content">
                                    <h4>{attempt.topic}</h4>
                                    <p className="activity-score">Score: {attempt.score}%</p>
                                    <span className="activity-date">
                                        {new Date(attempt.timestamp?.toDate()).toLocaleDateString()}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="no-activity">
                        <FaHistory className="empty-icon" />
                        <p>No quiz attempts yet. Start your learning journey!</p>
                        <button 
                            className="action-button primary"
                            onClick={handleStartQuiz}
                        >
                            Take Your First Quiz
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Dashboard; 